import React from 'react';
import {useWavRecorder} from "./hooks/useWavRecorder";
import {useWebsocket} from "../../base/hooks";
import {RoomQRCode} from "./RoomQRCode";
import {useParams, useNavigate} from 'react-router-dom';
import { Loader2 } from "lucide-react";
import {CopySessionListenLinkButton} from "./CopySessionListenLinkButton";
import {useApiClient} from "../../base/hooks/useApiClient";
import {useAsync} from "react-use";
import {EventType} from "../../../apis";
import {Page} from "../../layout/Page";
import {SessionStateProvider} from "./SessionStateContext";
import {useSessionEventListener} from "../../base/hooks/useSessionEventListener";
import {useWebsocketContext, WebsocketProvider} from "./WebsocketContext";
import {Stenographer} from "../../base";
import {SessionStartStopButton} from "./SessionStartStopButton";
import {useSupportedLanguages} from "../../base/hooks/useSupportedLanguages";
import { SettingsButton } from './SettingsButton';
import {InputDeviceProvider} from "./InputDeviceContext";
import {ZoomProvider} from "./ZoomContext";

export function SessionPageContent() {
    const { sessionUuid } = useParams<{ sessionUuid: string }>();
    const recorder = useWavRecorder();
    const apiClient = useApiClient();
    const { websocket } = useWebsocketContext();
    const { supportedLanguages } = useSupportedLanguages();
    const navigate = useNavigate();

    const { value: session, loading: sessionLoading } = useAsync(async () => {
        const response = await apiClient.get(`sessions/${sessionUuid}`);

        if (!response.ok) {
            if (response.status === 404) {
                navigate('/panel/not-found', { replace: true });
                return null;
            }
            console.error('Error fetching session data');
            return null;
        }

        const session = await response.json();

        return session as { name: string, originalLanguage: string, translationLanguages: string[] };
    });

    useSessionEventListener(EventType.ForceStopSession, async () => {
        await recorder.stop();
    });

    const onStartPlaying = async () => {
        await websocket.send(EventType.StartSession);

        await recorder.start((data) => {
            websocket.send(EventType.Audio, data.mono);
        });
    };

    const onStopPlaying = async () => {
        await recorder.stop();
        await websocket.send(EventType.StopSession);
    };

    const originalLanguageOption = supportedLanguages.find(language => {
        return language.value === session?.originalLanguage;
    });

    if (sessionLoading || !session) {
        return (
            <Page>
                <div className="fixed inset-0 bg-white bg-opacity-80 z-50 flex items-center justify-center">
                    <Loader2 className="h-12 w-12 animate-spin text-gray-600" />
                </div>
            </Page>
        );
    }

    return (
        <Page>
            <div className="max-w-4xl mx-auto p-6">
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="flex flex-wrap justify-between items-center">
                        <div className="flex items-center space-x-4">
                            <h2 className="text-2xl font-bold text-gray-900">{session.name}</h2>
                            {originalLanguageOption && (
                                <div className="flex items-center bg-gray-100 px-3 py-1 rounded-full">
                                    <div className="w-5 h-3.5 overflow-hidden rounded shadow-sm">
                                        <originalLanguageOption.Flag />
                                    </div>
                                    <span className="ml-2 text-sm font-medium text-gray-700">
                                        {originalLanguageOption.label}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-wrap space-x-4 items-center">
                            <SettingsButton />
                            <RoomQRCode />
                            <CopySessionListenLinkButton />
                            <SessionStartStopButton
                                onStart={onStartPlaying}
                                onStop={onStopPlaying}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex-1 mt-[5px]">
                    <Stenographer
                        eventType={EventType.Transcript}
                        className="min-h-[400px] max-h-[calc(100vh-320px)]"
                        placeholderTextAppear="Transcript will appear here..."
                    />
                </div>
            </div>
        </Page>
    );
}

export function SessionPage() {
    const { sessionUuid } = useParams<{ sessionUuid: string }>();
    const websocket = useWebsocket(`/sessions/${sessionUuid}`);

    return (
        <WebsocketProvider websocket={websocket}>
            <SessionStateProvider>
                <ZoomProvider>
                    <InputDeviceProvider>
                        <SessionPageContent />
                    </InputDeviceProvider>
                </ZoomProvider>
            </SessionStateProvider>
        </WebsocketProvider>
    );
}
